import axiosClient from "../api/axiosClient";

export type RepaymentFrequency = "monthly" | "weekly" | "biweekly" | string;

export type CreateLoanPayload = {
  borrower_id: string | number;
  vehicle_id?: string | number | null;
  loan_type: string;
  loan_amount: number;
  interest_rate?: number;
  tenure_months?: number;
  start_date?: string; 
  repayment_frequency?: RepaymentFrequency;
  documents?: File[]; 
};

export type UpdateLoanPayload = Partial<CreateLoanPayload> & { id: string | number };

export type LoanItem = {
  id: string | number;
  loan_id?: number;
  borrower_id?: string | number;
  vehicle_id?: string | number | null;
  loan_type?: string;
  loan_amount?: number;
  interest_rate?: number;
  tenure_months?: number;
  start_date?: string;
  repayment_frequency?: RepaymentFrequency;
  status?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: any;
    created_by_id:string | number;
  created_by_role:string | number;
  loan_created_date:string | number;
};

// export type ApiResponse<T = any> = {
//   success?: boolean;
//   data?: T;
//   message?: string;
// };
export type ApiSuccess<T> = {
  success: true;
  data: T;
};

export type ApiFailure = {
  success: false;
  message?: string;
};

export type ApiResponse<T = any> = ApiSuccess<T> | ApiFailure;

export type PaginatedResponse<T = any> = {
  items: T[];
  meta?: {
    current_page?: number;
    last_page?: number;
    per_page?: number;
    total?: number;
    from?: number;
    to?: number;
    first_page_url?: string;
    last_page_url?: string;
    next_page_url?: string | null;
    prev_page_url?: string | null;
    [key: string]: any;
  };
};

export type ApiTransaction = {
  transaction_id: number;
  loan_id: number;
  schedule_id: number | null;
  user_id: number;
  processed_by?: number | null;
  processed_by_id?: string | number;
  amount_paid: string;
  payment_mode?: string | null;
  reference_number?: string | null;
  status?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  loan?: any; 
  schedule?: any;
  processed_by_role?: string | null;
  adjustment_amount?:any;
  additional_amount?:any;
  emi_amount?:any;
};

export type ListTransactionsResponse = {
  total: number;
  transactions: ApiTransaction[];
};
/* -----------------------
   helper: normalize
   ----------------------- */
export function normalizeLoanItem(raw: any): LoanItem {
  if (!raw || typeof raw !== "object") return raw;
  return {
    ...raw,
    loan_id: raw.loan_id ?? (typeof raw.id === "number" ? raw.id : raw.loan_id),
    id: raw.id ?? raw.loan_id ?? undefined,
  } as LoanItem;
}

/* -----------------------
   API functions (exported)
   ----------------------- */
export async function createLoan(payload: CreateLoanPayload): Promise<ApiResponse<LoanItem>> {
  try {
    const { documents, ...rest } = payload as any;

    if (documents && Array.isArray(documents) && documents.length > 0) {
      const formData = new FormData();
      Object.keys(rest).forEach((k) => {
        const v = rest[k];
        if (v !== undefined && v !== null) {
          formData.append(k, String(v));
        }
      });
      documents.forEach((file: File) => formData.append("documents[]", file, file.name));
      const res = await axiosClient.post("/loans", formData);
      return { success: true, data: normalizeLoanItem(res.data) };
    } else {
      const res = await axiosClient.post("/loans", rest);
      return { success: true, data: normalizeLoanItem(res.data) };
    }
  } catch (err: any) {
    const message = err?.response?.data?.message ?? err?.message ?? "Failed to create loan";
    return { success: false, message };
  }
}

export async function getLoan(id: string | number): Promise<ApiResponse<LoanItem>> {
  try {
    const res = await axiosClient.get(`/loans/${id}`);
    return { success: true, data: normalizeLoanItem(res.data) };
  } catch (err: any) {
    const message = err?.response?.data?.message ?? err?.message ?? "Failed to fetch loan";
    return { success: false, message };
  }
}

export async function listLoans(
  params?: Record<string, any>
): Promise<ApiResponse<PaginatedResponse<LoanItem>>> {
  try {
    const res = await axiosClient.get("/loans", { params });
    const payload = res.data ?? {};

    const rawItems = payload.data ?? [];
    const items = rawItems.map(normalizeLoanItem);

    const meta = payload.meta ?? {
      current_page: payload.current_page,
      last_page: payload.last_page,
      per_page: payload.per_page,
      total: payload.total,
      from: payload.from,
      to: payload.to,
      next_page_url: payload.next_page_url,
      prev_page_url: payload.prev_page_url,
      first_page_url: payload.first_page_url,
      last_page_url: payload.last_page_url,
    };

    return { success: true, data: { items, meta } };
  } catch (err: any) {
    return {
      success: false,
      message:
        err?.response?.data?.message ??
        err?.message ??
        "Failed to list loans"
    };
  }
}


export async function updateLoan(payload: UpdateLoanPayload): Promise<ApiResponse<LoanItem>> {
  try {
    const { id, documents, ...rest } = payload as any;

    if (!id) return { success: false, message: "Loan id required" };

    if (documents && Array.isArray(documents) && documents.length > 0) {
      const formData = new FormData();
      Object.keys(rest).forEach((k) => {
        const v = rest[k];
        if (v !== undefined && v !== null) formData.append(k, String(v));
      });
      documents.forEach((file: File) => formData.append("documents[]", file, file.name));
      const res = await axiosClient.post(`/loans/${id}`, formData, {
        headers: { "X-HTTP-Method-Override": "PUT" },
      });
      return { success: true, data: normalizeLoanItem(res.data) };
    } else {
      const res = await axiosClient.put(`/loans/${id}`, rest);
      return { success: true, data: normalizeLoanItem(res.data) };
    }
  } catch (err: any) {
    const message = err?.response?.data?.message ?? err?.message ?? "Failed to update loan";
    return { success: false, message };
  }
}

export async function deleteLoan(id: string | number): Promise<ApiResponse> {
  try {
    const res = await axiosClient.delete(`/loans/${id}`);
    return { success: true, data: res.data };
  } catch (err: any) {
    const message = err?.response?.data?.message ?? err?.message ?? "Failed to delete loan";
    return { success: false, message };
  }
}


export default {
  createLoan,
  getLoan,
  listLoans,
  updateLoan,
  deleteLoan,
};


export const getloanNominees = async ( ) => {
  const res = await axiosClient.get(`/nominees`);
  return res.data;
};


export const getloanNominee = async (profile_id?: string | number, loan_id?: string | number) => {
  const params: Record<string, any> = {};
  if (profile_id != null) params.profile_id = profile_id;
  if (loan_id != null) params.loan_id = loan_id;

  const res = await axiosClient.get("/nominee-by-profile-loan", { params });
  return res.data;
};

export const createNominee = async (payload: Record<string, any>) => {
  try {
    const res = await axiosClient.post("/nominees", payload);
    return { success: true, data: res.data };
  } catch (err: any) {
    const message = err?.response?.data?.message ?? err?.message ?? "Failed to create nominee";
    return { success: false, message };
  }
};

/**
 * Update an existing nominee (PUT /nominees/:id)
 * id: nominee id
 * payload: fields to update
 */
export const updateNominee = async (id: string | number, payload: Record<string, any>) => {
  try {
    const res = await axiosClient.put(`/nominees/${id}`, payload);
    return { success: true, data: res.data };
  } catch (err: any) {
    const message = err?.response?.data?.message ?? err?.message ?? "Failed to update nominee";
    return { success: false, message };
  }
};

/**
 * Delete nominee (DELETE /nominees/:id)
 */
export const deleteNominee = async (id: string | number) => {
  try {
    const res = await axiosClient.delete(`/nominees/${id}`);
    return { success: true, data: res.data };
  } catch (err: any) {
    const message = err?.response?.data?.message ?? err?.message ?? "Failed to delete nominee";
    return { success: false, message };
  }
};



export const getBalanceDue = async (id: string | number ) => {
  const res = await axiosClient.get(`/loans/${id}/settlement`);
  return res.data;
};


export const getTransactions = async (params?: {
  [k: string]: any;
}): Promise<ListTransactionsResponse> => {
  const res = await axiosClient.get("/transactions", { params });
  return res.data as ListTransactionsResponse;
};

export const getTransactionReceipt = async (transactionId: number | string) => {
  try {
    const res = await axiosClient.get(`/transactions/${transactionId}`, {
      responseType: "blob",
    });
    return { success: true, blob: res.data as Blob, headers: res.headers };
  } catch (err: any) {
    const message = err?.response?.data?.message ?? err?.message ?? "Failed to fetch receipt";
    return { success: false, message };
  }
};


export const settlement = async (id: string | number, payload: Record<string, any>) => {
  try {
    const res = await axiosClient.post(`/loans/${id}/settle`, payload);
    return {
      success: true,
      data: res.data,
      status: res.status,
      headers: res.headers,
    };
  } catch (err: any) {
    const message =
      err?.response?.data?.message ??
      err?.response?.data ??
      err?.message ??
      "Failed to settle loan";
    return {
      success: false,
      message: String(message),
      error: err,
    };
  }
};


// Documents

export const getloanDocuments = async (id: string | number) => {
  const res = await axiosClient.get(`/loans/${id}/documents`);
  return res;
};
export const deleteDocuments = async (id: string | number) => {
  try {
    const res = await axiosClient.delete(`/loans/${id}/documents`);
    return { success: true, data: res.data };
  } catch (err: any) {
    const message = err?.response?.data?.message ?? err?.message ?? "Failed to delete nominee";
    return { success: false, message };
  }
};

export interface SettlementReceiptResponse {
  status: boolean;
  message: string;
  receipt: {
    receipt_no: string;
    settlement_date: string;
  };
  transaction: {
    transaction_id: number;
    amount_paid: string;
    additional_amount: string;
    adjustment_amount: string;
    payment_mode: string;
    status: string;
    created_at: string;
  };
  profile: any;
  loan: any;
  vehicle: any;
}


export const getloanSettlementReceipt = async (
  loanId: number
): Promise<SettlementReceiptResponse> => {
  const res = await axiosClient.get<SettlementReceiptResponse>(
    `/loans/${loanId}/settlement-receipt`
  );
  return res.data;
};